import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useCapaAccess, canUpdateCapaRecord } from "./capaAccess";
import { fetchCapaById, fetchCapaConfig, saveCapa } from "./capaService";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import TextEditor from "../../common/TextEditor/TextEditor";
import Dropdown from "../../common/dropDown";
import { useLoader } from "../../../context/LoaderContext";
import { CAPA_RISK_OPTIONS } from "./capaConstants";
import "./CapaPages.scss";

function optionFromValue(options, value) {
  const v = value == null ? "" : String(value);
  return options.find((o) => o.value === v) || options[0];
}

const CapaEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData, isSystemAdmin } = useCapaAccess();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { showLoader, hideLoader } = useLoader();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sourceType, setSourceType] = useState(null);
  const [sourceTypeOptions, setSourceTypeOptions] = useState([]);
  const [sourceRef, setSourceRef] = useState("");
  const [riskLevel, setRiskLevel] = useState(CAPA_RISK_OPTIONS[1]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailLoadAttempted, setDetailLoadAttempted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!userData?.OrganizationId) return;
      try {
        const cfg = await fetchCapaConfig(userData);
        const fromApi = Array.isArray(cfg?.sourceTypes) ? cfg.sourceTypes : [];
        const mapped = fromApi
          .map((s) => String(s || "").trim())
          .filter(Boolean)
          .map((v) => ({ value: v, label: v }));
        if (!cancelled && mapped.length > 0) {
          setSourceTypeOptions(mapped);
          setSourceType((prev) => optionFromValue(mapped, prev?.value || ""));
        }
      } catch {
        /* keep empty when config unavailable */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.OrganizationId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setDetailLoadAttempted(false);
      showLoader("Loading CAPA details...");
      try {
        const { detail: d } = await fetchCapaById(id, userData);
        if (!cancelled) {
          setDetail(d || null);
          if (d) {
            setTitle(d.Title || "");
            setDescription(d.Description || "");
            setSourceType(optionFromValue(sourceTypeOptions, d.SourceType));
            setSourceRef(d.SourceRef || "");
            setRiskLevel(optionFromValue(CAPA_RISK_OPTIONS, d.Risk));
          }
        }
      } finally {
        if (!cancelled) {
          setDetailLoadAttempted(true);
          setDetailLoading(false);
        }
        hideLoader();
      }
    })();
    return () => {
      cancelled = true;
      hideLoader();
    };
  }, [hideLoader, id, showLoader, sourceTypeOptions, userData]);

  const handleSave = async () => {
    await saveCapa(
      {
        CapaId: id,
        Title: title.trim(),
        Description: description ?? "",
        sourceType: sourceType?.value,
        sourceRef: sourceRef.trim(),
        risk: riskLevel?.value,
      },
      userData,
    );
    showAlert({
      title: "CAPA",
      message: "CAPA updated successfully.",
      type: "success",
    });
  };

  const canEditThisCapa = canUpdateCapaRecord({
    capaRow: detail,
    userData,
    isSystemAdmin,
  });

  if (!userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  if (detailLoading) {
    return (
      <div className="capa-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "CAPA", path: "/capa" },
            { label: "Edit" },
          ]}
        />
      </div>
    );
  }

  if (!detail && detailLoadAttempted) {
    return (
      <div className="capa-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "CAPA", path: "/capa" },
            { label: "Edit" },
          ]}
        />
        <div className="alert alert-warning mt-3">CAPA not found.</div>
      </div>
    );
  }

  if (detail && !canEditThisCapa) {
    return <Navigate to="/notification-center" replace />;
  }

  return (
    <div className="capa-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "CAPA", path: "/capa" },
          { label: detail.CapaNo, path: `/capa/${id}` },
          { label: "Edit" },
        ]}
      />

      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Title</label>
            <input
              className="form-control"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Description</label>
            <TextEditor
              value={description}
              onChange={setDescription}
              placeholder="Enter description..."
              height="14rem"
              minHeight="12rem"
              showImageButton={false}
            />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-4">
              <label className="form-label">Source type</label>
              <Dropdown
                options={sourceTypeOptions}
                selected={sourceType}
                onSelect={setSourceType}
              />
            </div>
            <div className="col-md-8">
              <label className="form-label">Source reference</label>
              <input
                type="text"
                className="form-control"
                value={sourceRef}
                onChange={(e) => setSourceRef(e.target.value)}
                placeholder="Optional external reference"
              />
            </div>
            <div className="col-12">
              <label className="form-label">Risk level</label>
              <div
                className="capa-risk-level"
                role="radiogroup"
                aria-label="Risk level"
              >
                {CAPA_RISK_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`capa-risk-pill capa-risk-pill--${opt.value.toLowerCase()} ${
                      riskLevel?.value === opt.value ? "is-selected" : ""
                    }`}
                    onClick={() => setRiskLevel(opt)}
                    aria-pressed={riskLevel?.value === opt.value}
                  >
                    <span className="capa-risk-pill__dot" aria-hidden />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate(`/capa/${id}`)}
            >
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>
              Update
            </button>
          </div>
        </div>
      </div>

      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
    </div>
  );
};

export default CapaEdit;
